/**
 * 本人成績(成績表)の数字。現行スプレッドシート「2025年【オペレーター】数字確認シート(スタッフ用)」の「成績表」タブの式を再現する
 * (2026-09-18 発注者共有。式は シートの各セルで確認。docs/specs/staff-report.md)。ここは純粋関数だけ(Firestore に触らない)。
 *
 * 元データ:
 *  - 日次【集計】と同じ行(StaffMetrics。daily_summary から staffMetrics で作る)… 実稼働・取得・通話・後処理
 *  - シフト提出(shift_submissions)… 提出シフト時間 = 本人が出した時間(管理者が直した日は最初の変更の「変更前」)、
 *    修正シフト時間 = シフト表のいまのセル(D-86 と同じ。提出されたもの＋管理者の修正を確定とする。P-45 → D-130 で決定)
 *
 * シートとの対応(成績表タブ):
 *  - 目標 = 修正シフト時間 × 2.0件(D29 =E28*2)。**不用品の日は3時間で1件**(D-75。シフト表と同じ)
 *  - 目標対比 = 取得 − 目標(目標がある日だけ。E34)/ 目標達成率 = 取得 ÷ 目標(合計は 取得合計 ÷ 目標合計。D35)
 *  - 1時間あたり取得件数 = 取得 ÷ 実稼働(E36。BB稼働ではない)
 *  - 通話時間Av・後処理時間Av・1時間あたりの通話回数 = 日次【集計】のその日の値。合計欄は日ごとの値の平均(D39〜D41 =AVERAGE)
 *  - 目標残取得件数 = 取得合計 − 目標合計(H14)
 *  - 月別: 実稼働・取得は月の合計(SUMIF)、実稼働目標件数 = 実稼働 × 2.0件(不用品は3時間1件)、1時間取得件数 = 取得 ÷ 実稼働
 *  - 平均1時間取得件数 = その月の全員・全日の「1人1日の 取得 ÷ 実稼働」の平均(R22 =AVERAGEIF(月, CA列))
 *  - 全体平均比較: 1人ずつの月の値(1時間取得件数 = 取得合計÷実稼働合計、ほかは日ごとの値の平均)を出し、全員の平均と本人(自数)を並べる
 *    (シートの「在宅(時給)各月結果」タブ。「1通話あたりの通話時間」は式が 1取得あたりの通話時間(AA列)のため、画面では正しい名前にした)
 */
import { derived, sumAgg, weekOfMonth, type Aggregate, type StaffMetrics } from '@core/dashboard/calc';
import type { BusinessType, DateStr, MonthStr } from '@core/lib/types';
import { beforeHoursOf } from './shiftDisplay';
import { TARGET_PER_HOUR_BY_BUSINESS } from './shiftRepo';
import { dayHours, type ShiftChangeLog, type ShiftSubmission } from './shiftSubmitRepo';

// ---- 年度(4月始まり) -----------------------------------------------------------

/** その月が入る年度(4月〜翌3月)。2026-03 → 2025 */
export function fiscalYearOf(month: MonthStr): number {
  const y = Number(month.slice(0, 4));
  return Number(month.slice(5, 7)) >= 4 ? y : y - 1;
}

/** 年度の12か月(4月〜翌3月) */
export function fiscalMonths(fy: number): MonthStr[] {
  return Array.from({ length: 12 }, (_, i) => {
    const m = ((3 + i) % 12) + 1;
    const y = m >= 4 ? fy : fy + 1;
    return `${y}-${String(m).padStart(2, '0')}` as MonthStr;
  });
}

export function daysOfMonth(month: MonthStr): DateStr[] {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: last }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}` as DateStr);
}

// ---- 共通 -------------------------------------------------------------------------

/** 1時間あたりの目標件数(給湯器 2.0件・不用品 3時間1件) */
const rateOf = (b: BusinessType) => TARGET_PER_HOUR_BY_BUSINESS[b];

const mean = (values: (number | null)[]): number | null => {
  const xs = values.filter((v): v is number => v !== null && Number.isFinite(v));
  return xs.length > 0 ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
};
const ratio = (a: number, b: number): number | null => (b > 0 ? a / b : null);

/** 1人1日の集計(BB ID・会社をまとめる)。key = `${op_id}|${date}` */
function byPersonDay(rows: StaffMetrics[]): Map<string, { op_id: string; date: DateStr; agg: Aggregate; target: number }> {
  const list = new Map<string, StaffMetrics[]>();
  for (const r of rows) {
    const k = `${r.op_id}|${r.date}`;
    const l = list.get(k);
    if (l) l.push(r);
    else list.set(k, [r]);
  }
  const out = new Map<string, { op_id: string; date: DateStr; agg: Aggregate; target: number }>();
  for (const [k, l] of list) {
    out.set(k, { op_id: l[0]!.op_id, date: l[0]!.date, agg: sumAgg(l), target: l.reduce((s, r) => s + r.worked_hours * rateOf(r.business), 0) });
  }
  return out;
}

/** 1人1日の指標(日次【集計】の列と同じ) */
function dayMetrics(agg: Aggregate) {
  const d = derived(agg);
  return {
    hours: agg.hours,
    appointments: agg.appointments,
    calls: agg.calls,
    /** 取得 ÷ 実稼働(成績表の「1時間あたり取得件数」) */
    perHour: ratio(agg.appointments, agg.hours),
    talkAvg: d.talk_avg,
    afterAvg: d.after_avg,
    callsPerHour: d.calls_per_hour,
    talkPerAppt: d.talk_per_appt,
  };
}

// ---- シフト(提出・修正) ------------------------------------------------------------

export type ShiftDay = {
  /** 本人が出した時間(h)。出していない日は null */
  submitted: number | null;
  /** シフト表のいまの時間(h)。入っていない日は null。欠勤・当日欠勤は 0 */
  corrected: number | null;
  /** 欠勤・当日欠勤の印 */
  absent: 'absent' | 'same_day' | null;
  /** 目標件数(修正シフト時間 × 1時間の目標件数) */
  target: number | null;
};

/**
 * 日(01〜31) → 提出・修正・目標。
 * @param businessOfBbId その日の BB ID の架電内容(不用品は3時間1件で目標を出す)
 */
export function shiftDaysOf(sub: Pick<ShiftSubmission, 'days' | 'logs' | 'bb_id'> | null, businessOfBbId: (bbId: string) => BusinessType): Map<string, ShiftDay> {
  const out = new Map<string, ShiftDay>();
  if (!sub) return out;
  // 管理者が直した日は、その日の最初の変更の「変更前」が提出(logs は新しい順なので後ろで上書き)
  const firstBefore = new Map<string, ShiftChangeLog>();
  for (const l of sub.logs ?? []) firstBefore.set(l.day, l);
  for (const dd of new Set([...Object.keys(sub.days), ...firstBefore.keys()])) {
    const day = sub.days[dd];
    // シフト表 D-86 の submittedHoursOf と同じ数え方。0h は「出していない」として空欄にする
    const submitted = firstBefore.has(dd) ? beforeHoursOf(firstBefore.get(dd)!) : dayHours(day);
    const corrected = day ? dayHours(day) : null;
    const business = businessOfBbId(day?.bb_id ?? sub.bb_id);
    out.set(dd, {
      submitted: submitted > 0 ? submitted : null,
      corrected,
      absent: day?.status ?? null,
      target: corrected !== null && corrected > 0 ? Math.round(corrected * rateOf(business) * 10) / 10 : null,
    });
  }
  return out;
}

// ---- 日割り結果 ------------------------------------------------------------------

export type DailyLine = ShiftDay & {
  date: DateStr;
  /** その日の日次【集計】が無い(稼働なし)ときは null */
  hours: number | null;
  appointments: number | null;
  /** 目標対比 = 取得 − 目標(目標がある日だけ) */
  diff: number | null;
  /** 目標達成率(%) */
  achievement: number | null;
  perHour: number | null;
  calls: number | null;
  talkAvg: number | null;
  afterAvg: number | null;
  callsPerHour: number | null;
};

export type DailyTotal = {
  submitted: number;
  corrected: number;
  target: number;
  hours: number;
  appointments: number;
  diff: number;
  achievement: number | null;
  perHour: number | null;
  calls: number;
  /** 日ごとの値の平均(シートの合計欄 =AVERAGE) */
  talkAvg: number | null;
  afterAvg: number | null;
  callsPerHour: number | null;
};

/**
 * @param importedDates 日次集計がある日(CSV取込済み)。**集計の無い日(まだ来ていない日・CSV未取込の日)は対比・達成率を出さない**
 *                      (シートでは取得0として −18 のように出るが、未取込と「働かなかった」を区別するため)。
 *                      集計はあるのに本人の行が無い日は、取得0・実稼働0 として対比を出す
 */
export function dailyReport(month: MonthStr, myRows: StaffMetrics[], shifts: Map<string, ShiftDay>, importedDates?: Set<DateStr>): { lines: DailyLine[]; total: DailyTotal } {
  const days = byPersonDay(myRows.filter((r) => r.date.startsWith(month)));
  const byDate = new Map([...days.values()].map((d) => [d.date, d]));
  const lines: DailyLine[] = daysOfMonth(month).map((date) => {
    const s = shifts.get(date.slice(8, 10)) ?? { submitted: null, corrected: null, absent: null, target: null };
    const pd = byDate.get(date);
    /** 取得件数が確定した日(対応履歴を取り込んだ日)。未取込の日は 0 ではなく空欄にする */
    const confirmed = importedDates ? importedDates.has(date) : true;
    const m = pd ? dayMetrics(pd.agg) : null;
    const target = s.target !== null && s.target > 0 ? s.target : null;
    // シフトがある日は、確定していれば取得0として数える(働かなかった)。確定前の日は空欄
    const appts = !confirmed ? null : m ? m.appointments : target !== null ? 0 : null;
    return {
      ...s,
      date,
      hours: m ? m.hours : null,
      appointments: appts,
      diff: target === null || appts === null ? null : appts - target,
      achievement: target === null || appts === null ? null : (appts / target) * 100,
      perHour: m ? m.perHour : null,
      calls: m ? m.calls : null,
      talkAvg: m ? m.talkAvg : null,
      afterAvg: m ? m.afterAvg : null,
      callsPerHour: m ? m.callsPerHour : null,
    };
  });
  return { lines, total: totalOfLines(lines) };
}

/** 日割りの行の合計(月の合計・週の合計に使う)。通話時間Av などは日ごとの値の平均 */
export function totalOfLines(lines: DailyLine[]): DailyTotal {
  const sum = (pick: (l: DailyLine) => number | null) => lines.reduce((a, l) => a + (pick(l) ?? 0), 0);
  const target = sum((l) => l.target);
  const appointments = sum((l) => l.appointments);
  const hours = sum((l) => l.hours);
  return {
      submitted: sum((l) => l.submitted),
      corrected: sum((l) => l.corrected),
      target,
      hours,
      appointments,
      diff: sum((l) => l.diff),
      achievement: target > 0 ? (appointments / target) * 100 : null,
      perHour: ratio(appointments, hours),
      calls: sum((l) => l.calls),
      talkAvg: mean(lines.map((l) => l.talkAvg)),
      afterAvg: mean(lines.map((l) => l.afterAvg)),
      callsPerHour: mean(lines.map((l) => l.callsPerHour)),
  };
}

// ---- 週(月曜始まり・月内通番 W1〜W6。2026-10-15 発注者指示「週別」「推移グラフ」) ----------------

export type WeekRange = { no: number; from: DateStr; to: DateStr; dates: DateStr[] };

/** その月の週(月曜始まり・月内通番。月をまたぐ週は月の中だけ) */
export function weeksOfMonth(month: MonthStr): WeekRange[] {
  const out: WeekRange[] = [];
  for (const d of daysOfMonth(month)) {
    const no = weekOfMonth(d);
    const w = out.find((x) => x.no === no);
    if (w) {
      w.dates.push(d);
      w.to = d;
    } else out.push({ no, from: d, to: d, dates: [d] });
  }
  return out;
}

/** 日ごとの全体平均の1時間取得件数(その日に稼働した全員の「1人1日の 取得 ÷ 実稼働」の平均。月別の「平均1時間取得件数」と同じ数え方) */
export function dailyAvgPerHour(allRows: StaffMetrics[]): Map<DateStr, number | null> {
  const byDate = new Map<DateStr, (number | null)[]>();
  for (const d of byPersonDay(allRows).values()) {
    const l = byDate.get(d.date) ?? [];
    l.push(ratio(d.agg.appointments, d.agg.hours));
    byDate.set(d.date, l);
  }
  return new Map([...byDate].map(([date, xs]) => [date, mean(xs)]));
}

export type WeekLine = WeekRange &
  DailyTotal & {
    /** 全体平均の1時間取得件数(その週の全員・全日の平均) */
    avgPerHour: number | null;
    /** 取得件数が確定している日があるか(無い週はグラフで 0 ではなく空にする) */
    hasAppointments: boolean;
  };

/** 月の週ごとの結果(日割りの行を週でまとめる) */
export function weeklyReport(month: MonthStr, lines: DailyLine[], allRows: StaffMetrics[]): WeekLine[] {
  const pds = [...byPersonDay(allRows.filter((r) => r.date.startsWith(month))).values()];
  return weeksOfMonth(month).map((w) => {
    const set = new Set(w.dates);
    const ls = lines.filter((l) => set.has(l.date));
    return {
      ...w,
      ...totalOfLines(ls),
      hasAppointments: ls.some((l) => l.appointments !== null),
      avgPerHour: mean(pds.filter((d) => set.has(d.date)).map((d) => ratio(d.agg.appointments, d.agg.hours))),
    };
  });
}

// ---- 月別(年度)と全体平均比較 -------------------------------------------------------

export type MonthLine = {
  month: MonthStr;
  /** 本人 */
  hours: number;
  /** 実稼働目標件数 = 実稼働 × 1時間の目標件数 */
  hoursTarget: number;
  appointments: number;
  perHour: number | null;
  /** 全員・全日の「1人1日の 取得 ÷ 実稼働」の平均 */
  avgPerHourDaily: number | null;
};

export type CompareMetric = 'perHour' | 'talkPerAppt' | 'afterAvg' | 'callsPerHour';
export type CompareLine = { month: MonthStr; avg: Record<CompareMetric, number | null>; mine: Record<CompareMetric, number | null> };

/** 1人1か月の値: 1時間取得件数 = 取得合計 ÷ 実稼働合計、ほかは日ごとの値の平均 */
function personMonth(days: { agg: Aggregate }[]): Record<CompareMetric, number | null> {
  const ms = days.map((d) => dayMetrics(d.agg));
  const appts = ms.reduce((a, m) => a + m.appointments, 0);
  const hours = ms.reduce((a, m) => a + m.hours, 0);
  return {
    perHour: ratio(appts, hours),
    talkPerAppt: mean(ms.map((m) => m.talkPerAppt)),
    afterAvg: mean(ms.map((m) => m.afterAvg)),
    callsPerHour: mean(ms.map((m) => m.callsPerHour)),
  };
}

const METRICS: CompareMetric[] = ['perHour', 'talkPerAppt', 'afterAvg', 'callsPerHour'];

/**
 * @param allRows 年度の全員の行(日次【集計】と同じ)
 */
export function fiscalReport(fy: number, opId: string, allRows: StaffMetrics[]): { months: MonthLine[]; compare: CompareLine[] } {
  const pds = [...byPersonDay(allRows).values()];
  const months: MonthLine[] = [];
  const compare: CompareLine[] = [];
  for (const month of fiscalMonths(fy)) {
    const inMonth = pds.filter((d) => d.date.startsWith(month));
    const mine = inMonth.filter((d) => d.op_id === opId);
    const hours = mine.reduce((a, d) => a + d.agg.hours, 0);
    const appointments = mine.reduce((a, d) => a + d.agg.appointments, 0);
    months.push({
      month,
      hours,
      hoursTarget: Math.round(mine.reduce((a, d) => a + d.target, 0) * 10) / 10,
      appointments,
      perHour: ratio(appointments, hours),
      avgPerHourDaily: mean(inMonth.map((d) => ratio(d.agg.appointments, d.agg.hours))),
    });
    // 全体平均: 1人ずつの月の値を出してから全員で平均する
    const people = new Map<string, { agg: Aggregate }[]>();
    for (const d of inMonth) {
      const l = people.get(d.op_id);
      if (l) l.push(d);
      else people.set(d.op_id, [d]);
    }
    const values = [...people.entries()].map(([id, days]) => ({ id, v: personMonth(days) }));
    const avg = Object.fromEntries(METRICS.map((k) => [k, mean(values.map((x) => x.v[k]))])) as Record<CompareMetric, number | null>;
    const me = values.find((x) => x.id === opId)?.v ?? { perHour: null, talkPerAppt: null, afterAvg: null, callsPerHour: null };
    compare.push({ month, avg, mine: me });
  }
  return { months, compare };
}

// ---- 会社別(2026-10-15 発注者指示「どこの会社を何時間かけて何件取れているか」) -----------------------

export type CompanyLine = { companyId: string; hours: number; appointments: number; perHour: number | null };

/**
 * 期間の会社(割振)ごとの 実稼働・取得・1時間あたり。稼働時間の多い順。
 * 取得は対応履歴を取り込んだ日(importedDates)だけ数える(日割り結果と同じ。未取込の日は時間だけ入る)
 */
export function companyBreakdown(myRows: StaffMetrics[], dates: DateStr[], importedDates?: Set<DateStr>): CompanyLine[] {
  const set = new Set(dates);
  const by = new Map<string, { hours: number; appointments: number }>();
  for (const r of myRows) {
    if (!set.has(r.date)) continue;
    const k = r.company_id ?? '';
    const c = by.get(k) ?? { hours: 0, appointments: 0 };
    c.hours += r.worked_hours;
    if (!importedDates || importedDates.has(r.date)) c.appointments += r.appointments;
    by.set(k, c);
  }
  return [...by]
    .map(([companyId, c]) => ({ companyId, hours: Math.round(c.hours * 100) / 100, appointments: c.appointments, perHour: ratio(c.appointments, c.hours) }))
    .filter((c) => c.hours > 0 || c.appointments > 0)
    .sort((a, b) => b.hours - a.hours || b.appointments - a.appointments);
}
